import {
  CalendarIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";

import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { endOfDay, subDays } from "date-fns";
import { useEffect, useState } from "react";
import { classNames } from "../../shared/utility";
import Button from "../ui/Button";

const buildRange = (startDate, endDate) => [
  {
    startDate: startDate ? new Date(startDate) : subDays(new Date(), 30),
    endDate: endDate ? new Date(endDate) : new Date(),
    key: "selection",
  },
];

export default function DateRangeFilter(props) {
  const {
    applyChanges,
    onChange,
    onClear,
    startDate,
    endDate,
    maxDate,
    showDates,
    setShowDates,
    rangeSummary,
  } = props;

  const [state, setState] = useState(buildRange(startDate, endDate));
  const hasActiveDateFilter = Boolean(startDate && endDate);

  useEffect(() => {
    setState(buildRange(startDate, endDate));
  }, [startDate, endDate]);

  const applyRange = (nextStartDate, nextEndDate) => {
    onChange(nextStartDate, nextEndDate);
    setState(buildRange(nextStartDate, nextEndDate));
  };

  const handleDateChange = (ranges) => {
    let { startDate, endDate } = ranges.selection;

    if (startDate.toDateString() === endDate.toDateString()) {
      endDate = endOfDay(endDate);
    }

    applyRange(startDate, endDate);
  };

  const clearDates = () => {
    if (onClear) {
      onClear();
    } else {
      onChange(null, null);
    }
    setState(buildRange(null, null));
    // Keep the picker open so the user can confirm with Apply date.
    // This avoids sending stale date values before React state updates.
  };

  const buttonLabel = hasActiveDateFilter
    ? rangeSummary || "Date range"
    : "No dates";

  return (
    <div className="relative -ml-px block">
      <Button
        type="button"
        variant="secondary"
        size="md"
        className="relative min-h-10 shrink-0 px-3.5 text-sm"
        onClick={() => setShowDates(!showDates)}
      >
        <CalendarIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="max-w-[14rem] truncate whitespace-nowrap sm:max-w-none">
          {buttonLabel}
        </span>
        {hasActiveDateFilter ? (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-[#16A34A]"
            aria-hidden="true"
          />
        ) : null}
        <ChevronDownIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      </Button>

      <div
        className={classNames(
          showDates ? null : "hidden",
          "absolute right-0 z-50 mt-2 max-w-[calc(100vw-2rem)] origin-top-right rounded-2xl border border-[#E2E8F0] bg-white shadow-xl focus:outline-none",
        )}
      >
        <div className="flex items-center justify-between rounded-t-2xl border-b border-[#E2E8F0] bg-[#F6F8FB] px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-[#0F172A]">Date range</p>
          </div>
          <button
            type="button"
            onClick={() => setShowDates(false)}
            className="rounded-full p-1.5 text-[#64748B] transition hover:bg-white hover:text-[#0F172A]"
            aria-label="Close date filter"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-x-auto bg-white p-2">
          <DateRangePicker
            onChange={handleDateChange}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            months={2}
            ranges={state}
            direction="horizontal"
            maxDate={maxDate}
          />
        </div>

        <div className="flex flex-wrap justify-between gap-2 rounded-b-2xl border-t border-[#E2E8F0] bg-white p-3">
          <Button type="button" variant="ghost" onClick={clearDates}>
            Clear dates
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowDates(false)}
            >
              Close
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                applyChanges(state[0].startDate, state[0].endDate);
                setShowDates(false);
              }}
            >
              Apply date
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
